<template>
  <section>
    <div class="ca-bullet-container">
      <div class="ca-overlapping-carousel flex flex-col items-center">
        <component
          v-if="title"
          :is="titleHeadingTag"
          class="ca-overlapping-carousel__heading"
          :class="titleTypography"
        >
          {{ title }}
        </component>
        <div
          class="ca-overlapping-carousel__viewport flex items-center justify-between"
          role="region"
          aria-label="Carrossel de conteúdos"
          aria-live="polite"
        >
          <div
            class="ca-overlapping-carousel__stage"
            :class="
              dir === 'next'
                ? 'ca-overlapping-carousel__stage--next'
                : 'ca-overlapping-carousel__stage--prev'
            "
            ref="stageRef"
            role="region"
            aria-roledescription="carousel"
            aria-label="Carrossel de imagens"
            @scroll.passive="onScroll"
            @scrollend="onScrollEnd"
          >
            <div
              v-for="(entry, index) in renderedCards"
              :key="index"
              class="ca-overlapping-carousel__card"
              :class="positionClass(entry.realIndex)"
              :aria-hidden="
                entry.isClone || entry.realIndex !== activeIndex
                  ? 'true'
                  : undefined
              "
              role="group"
              aria-roledescription="slide"
              :aria-label="`Imagem ${entry.realIndex + 1} de ${cards.length}. ${entry.card.title}`"
            >
              <div class="ca-overlapping-carousel__media">
                <img
                  v-if="entry.card.imageUrl"
                  class="ca-overlapping-carousel__image"
                  :src="entry.card.imageUrl"
                  :alt="entry.card.imageAlt"
                />
                <div class="ca-overlapping-carousel__overlay"></div>
                <div
                  class="ca-overlapping-carousel__content flex flex-col justify-between p-size-5"
                >
                  <header
                    class="ca-overlapping-carousel__header flex items-center gap-size-4"
                  >
                    <RoundedAvatarV2
                      v-if="entry.card.iconUrl"
                      class="ca-overlapping-carousel__avatar"
                      :image="entry.card.iconUrl"
                      size="56px"
                      backgroundColorClass="backgrounds-surface"
                    />
                    <component
                      :is="
                        entry.isClone
                          ? 'p'
                          : normalizeHeadingTag(
                              entry.card.titleHeadingType,
                              'p',
                            )
                      "
                      class="ca-overlapping-carousel__title heading-regular"
                    >
                      {{ entry.card.title }}
                    </component>
                  </header>
                  <div
                    v-if="entry.card.ctaText"
                    class="ca-overlapping-carousel__footer flex justify-center"
                  >
                    <BaseButton
                      class="inverted primary"
                      type="inverted"
                      variant="primary"
                      :label="entry.card.ctaText"
                      :tabindex="entry.realIndex === activeIndex ? '0' : '-1'"
                      @click="handleButtonClick(entry.card)"
                    />
                  </div>
                </div>
                <!-- White veil over the non-selected (side) cards. -->
                <div class="ca-overlapping-carousel__veil"></div>
              </div>
            </div>
          </div>

          <IconButton
            v-if="cards.length > 1"
            class="ca-overlapping-carousel__arrow ca-overlapping-carousel__arrow--prev"
            type="theme"
            variant="secondary"
            size="s"
            aria-label="Conteúdo anterior"
            @clicked="prev"
          >
            <template #trailingIcon>
              <ChevronLeft />
            </template>
          </IconButton>

          <IconButton
            v-if="cards.length > 1"
            class="ca-overlapping-carousel__arrow ca-overlapping-carousel__arrow--next"
            type="theme"
            variant="secondary"
            size="s"
            aria-label="Conteúdo seguinte"
            @clicked="next"
          >
            <template #trailingIcon>
              <ChevronRight />
            </template>
          </IconButton>
        </div>

        <CaPageControl
          v-if="cards.length > 1"
          class="ca-overlapping-carousel__dots"
          :count="cards.length"
          :model-value="activeIndex"
          @update:model-value="goTo"
        />
      </div>
    </div>
  </section>
</template>

<script>
const isDev = import.meta.env.DEV;
</script>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import {
  RoundedAvatarV2,
  BaseButton,
  IconButton,
} from "ca-online-design-system";
import CaPageControl from "../../base/CaPageControl.vue";
import ChevronLeft from "@assets/chevron-left.svg";
import ChevronRight from "@assets/chevron-right.svg";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaOverlappingCardsCarousel",
});

const props = defineProps({
  /** Title */
  title: {
    type: String,
    default: "",
  },
  titleHeadingType: {
    type: String,
    default: "H2",
  },
  /** Cards */
  cards: {
    type: Array,
    default: () => [],
  },
});

// Dev-only fallback used when the backend sends no data.
const mock = ref(null);
const title = computed(() => props.title || mock.value?.title || "");
const titleHeadingTag = computed(() =>
  normalizeHeadingTag(
    props.titleHeadingType || mock.value?.titleHeadingType,
  ),
);
const cards = computed(() =>
  props.cards.length ? props.cards : (mock.value?.cards ?? []),
);

const activeIndex = ref(0);
const stageRef = ref(null);
// Last navigation direction; drives which side retired cards wait on (mobile),
// so an incoming card always slides in from the correct edge (no cross-sweep).
const dir = ref("next");

// Deteta desktop (>= 1024px) para escolher a tipografia do título:
// mobile/tablet usam "paragraph-medium" e desktop usa "heading-small".
const isDesktop = ref(false);
const isMobile = ref(false);

const updateScreen = () => {
  isDesktop.value = window.innerWidth >= 1024;
  isMobile.value = window.innerWidth < 768;
};

const titleTypography = computed(() =>
  isDesktop.value ? "heading-large" : "heading-jumbo",
);

// Mobile loops infinitely by rendering the cards three times and parking the
// user in the middle copy; desktop uses a single set (the ring wraps in JS).
// Each entry keeps its real index so classes, dots and labels stay correct.
const renderedCards = computed(() => {
  const list = cards.value;
  if (isMobile.value && list.length > 1) {
    return [...list, ...list, ...list].map((card, i) => ({
      card,
      realIndex: i % list.length,
      isClone: i < list.length || i >= list.length * 2,
    }));
  }
  return list.map((card, i) => ({ card, realIndex: i, isClone: false }));
});

/**
 * Position of each card relative to the selected one, wrapped for the loop.
 *
 * The stack is a ring seen head-on: the selected card at the front, one card
 * peeking out either side, and every remaining card at the *back* of the ring
 * -- centred and smaller, and so completely covered by the front card. That
 * back slot is where cards retire to and where they emerge from: because it is
 * a single slot shared by both directions, a card that has just left on one
 * side is already standing exactly where it needs to be to come in on the
 * other, and nothing ever flies off the side of the stage or is teleported
 * across it between presses.
 */
function positionClass(index) {
  const total = cards.value.length;
  let rel = (((index - activeIndex.value) % total) + total) % total;
  if (rel > total / 2) rel -= total;
  if (rel === 0) return "ca-overlapping-carousel__card--active";
  if (rel === -1) return "ca-overlapping-carousel__card--prev";
  if (rel === 1) return "ca-overlapping-carousel__card--next";
  // Remaining cards retire off-stage to their own side (left/right).
  if (rel < 0) return "ca-overlapping-carousel__card--back-prev";
  return "ca-overlapping-carousel__card--back-next";
}

// Two-step move: point the retired cards at the entry side, commit it to the
// DOM and force a reflow, then change the index. Without the forced reflow the
// incoming card would interpolate from the old side and sweep across the stage.
async function navigate(delta) {
  const total = cards.value.length;
  if (!total) return;
  const target = (activeIndex.value + delta + total) % total;
  // Mobile: let the native scroll drive the move.
  if (isMobile.value) {
    scrollToCard(target);
    return;
  }
  dir.value = delta >= 0 ? "next" : "prev";
  await nextTick();
  void stageRef.value?.offsetHeight;
  activeIndex.value = target;
}
function next() {
  navigate(1);
}

function prev() {
  navigate(-1);
}

function goTo(index) {
  navigate(index - activeIndex.value);
}

/** Navigate to the card CTA URL when its button is clicked. */
function handleButtonClick(card) {
  const url = card?.ctaUrl;
  if (!url) return;
  if (card.ctaTarget === "_blank") {
    window.open(url, "_blank", "noopener");
  } else {
    window.location.href = url;
  }
}

// --- Mobile: native scroll-snap swipe with an infinite loop (3x clone set) ---
function getCardEls() {
  return Array.from(
    stageRef.value?.querySelectorAll(".ca-overlapping-carousel__card") || [],
  );
}

// Offset of the middle (real) copy inside the tripled render on mobile.
function loopOffset() {
  const total = cards.value.length;
  return isMobile.value && total > 1 ? total : 0;
}

function nearestRenderedIndex() {
  const el = stageRef.value;
  if (!el) return 0;
  const centre = el.scrollLeft + el.clientWidth / 2;
  let nearest = 0;
  let best = Infinity;
  getCardEls().forEach((card, i) => {
    const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - centre);
    if (distance < best) {
      best = distance;
      nearest = i;
    }
  });
  return nearest;
}

function scrollToRendered(renderedIndex, behavior = "smooth") {
  const el = stageRef.value;
  const card = getCardEls()[renderedIndex];
  if (!el || !card) return;
  el.scrollTo({
    left: card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2,
    behavior,
  });
}

function scrollToCard(index) {
  scrollToRendered(index + loopOffset());
}

// The finger drives the scroll; sync the active card (dots + aria) from it.
// The debounce recenters even on browsers without the `scrollend` event.
let scrollIdleTimer = null;
function onScroll() {
  if (!isMobile.value) return;
  const total = cards.value.length;
  if (!total) return;
  activeIndex.value = nearestRenderedIndex() % total;
  clearTimeout(scrollIdleTimer);
  scrollIdleTimer = setTimeout(recenterLoop, 120);
}

function onScrollEnd() {
  if (!isMobile.value) return;
  clearTimeout(scrollIdleTimer);
  recenterLoop();
}

// Once the swipe settles, jump back into the middle copy (no animation) so the
// user can keep swiping the same direction through every card forever.
function recenterLoop() {
  const total = cards.value.length;
  if (total <= 1) return;
  const rendered = nearestRenderedIndex();
  const real = ((rendered % total) + total) % total;
  activeIndex.value = real;
  if (rendered < total || rendered >= total * 2) {
    scrollToRendered(real + total, "auto");
  }
}

// Park the viewport on the active card within the middle copy.
function centreLoop() {
  if (!isMobile.value) return;
  scrollToRendered(activeIndex.value + loopOffset(), "auto");
}

onMounted(async () => {
  updateScreen();
  window.addEventListener("resize", updateScreen);
  // Only fall back to the mock when the backend sent no data at all.
  if (isDev && !props.cards.length) {
    const { mockOverlappingCardsCarousel } =
      await import("./mocks/overlappingCardsCarousel.js");
    mock.value = mockOverlappingCardsCarousel;
  }
  await nextTick();
  centreLoop();
});

// Re-park in the middle copy whenever the data changes or mobile toggles.
watch([cards, isMobile], async () => {
  await nextTick();
  centreLoop();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScreen);
  clearTimeout(scrollIdleTimer);
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Overlapping cards carousel (mobile first).
// One card fully visible at a time (100% width); slides horizontally via a
// translated track. Navigation with swipe and the CaPageControl dots.
// ---------------------------------------------------------------------------
.ca-overlapping-carousel {
  width: 100%;
  // Padding vertical: size-8 em mobile, size-11 em tablet/desktop.
  padding-block: var(--size-8);
  // Gap entre título, viewport e dots: size-8 em mobile, size-10 em tablet/desktop.
  gap: var(--size-8);
}

@media (min-width: 768px) {
  .ca-overlapping-carousel {
    padding-block: var(--size-11);
    gap: var(--size-10);
  }
}

.ca-overlapping-carousel__heading {
  margin: 0;
  text-align: center;
  color: var(--ca-color-text-headers);
}

.ca-overlapping-carousel__viewport {
  width: 100%;
}

.ca-overlapping-carousel__stage {
  // Mobile-first: native horizontal scroll with snap. The finger drives the
  // scroll and the browser snaps to the nearest card; JS only reads position.
  position: relative;
  // Full-bleed to the viewport edges, breaking out of .ca-bullet-container.
  width: calc(100% + ca-margin("base") * 2);
  max-width: none;
  margin-inline: calc(ca-margin("base") * -1);
  height: 440px;
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  gap: var(--size-5);
  overflow-x: auto;
  overflow-y: visible;
  scroll-snap-type: x mandatory;
  overscroll-behavior-inline: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
  // Symmetric padding centres the snapped card and reveals a tip on each side.
  padding-inline: ca-margin("base");
  scroll-padding-inline: ca-margin("base");
  // Ring geometry knobs consumed by the tablet/desktop overlap below.
  --side-shift: 107%;
  --side-scale: 1;
  --back-scale: 0.625;
  --side-veil: 0;
  --oc-ease: cubic-bezier(0.15, 0, 0.2, 1);
  --oc-duration: 0.65s;
  --oc-z-swap: 0;

  &::-webkit-scrollbar {
    display: none;
  }
}

.ca-overlapping-carousel__stage::-webkit-scrollbar {
  height: 0;
  width: 0;
  background: transparent;
}

.ca-overlapping-carousel__stage::-webkit-scrollbar-track {
  background: transparent;
}

// Off by default: the reference leaves its side cards at full colour. Kept as
// a single knob so the veil can be dialled back in without a code change.
.ca-overlapping-carousel__card:not(.ca-overlapping-carousel__card--active)
  .ca-overlapping-carousel__veil {
  opacity: var(--side-veil);
}

.ca-overlapping-carousel__card {
  // Mobile-first: a native snap item, centred with a tip of each neighbour
  // peeking. The absolute ring layout is restored at the tablet/desktop bp.
  position: relative;
  flex-shrink: 0;
  width: calc(100vw - ca-margin("base") * 3);
  height: 100%;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  user-select: none;
  color: inherit;

  &:focus-visible {
    outline: 2px solid var(--ca-color-outline-focus-only);
    outline-offset: 2px;
  }
}

// The overlapping "ring" (active / prev / next / back positions) is a
// tablet/desktop concern only; on mobile the cards are a flat scroll list.

// Side navigation arrows: mobile relies on swipe, so hide them there.
.ca-overlapping-carousel__arrow {
  display: none;
  flex-shrink: 0;

  // Match the BaseButton focus (theme secondary has no DS focus-visible rule).
  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
    outline-offset: 1px;
  }
}

// Tab order is stage (CTA) -> prev -> next; keep the prev arrow visually first.
.ca-overlapping-carousel__arrow--prev {
  order: -1;
}

.ca-overlapping-carousel__media {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--size-5);
  overflow: hidden;
  transition: filter 0.4s ease;
}

// Sombra apenas no cartão ativo: os laterais (blur + veil) ficam sem sombra
// para não acumularem cinzento entre cartões sobrepostos.
.ca-overlapping-carousel__card--active .ca-overlapping-carousel__media {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

// Fill the whole media box even when the source image is smaller.
// !important defeats the design-system preflight `img { height: auto }`.
.ca-overlapping-carousel__image {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  object-position: center;
}

// Non-selected side cards are blurred (tablet/desktop only; mobile stays basic).
@media (min-width: 768px) {
  .ca-overlapping-carousel__card:not(.ca-overlapping-carousel__card--active)
    .ca-overlapping-carousel__media {
    filter: blur(3px);
  }
}

// Subtle top gradient just for header legibility (avatar + title).
.ca-overlapping-carousel__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(51, 60, 69, 0.9) 0.03%,
    rgba(102, 102, 102, 0) 40.69%
  );
}

.ca-overlapping-carousel__content {
  position: relative;
  height: 100%;
}

.ca-overlapping-carousel__title {
  margin: 0;
  color: var(--ca-color-neutrals-inverted, #fff);
}

// Impede que o avatar seja comprimido quando o título quebra para 2 linhas.
.ca-overlapping-carousel__avatar {
  flex-shrink: 0;
}

// White veil that fades the non-selected side cards.
.ca-overlapping-carousel__veil {
  position: absolute;
  inset: 0;
  border-radius: var(--size-5);
  background: var(--ca-color-neutrals-inverted, #fff);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.4s ease;
}

// White veil that fades the non-selected side cards (tablet/desktop only).
@media (min-width: 768px) {
  .ca-overlapping-carousel__card:not(.ca-overlapping-carousel__card--active)
    .ca-overlapping-carousel__veil {
    opacity: 0.55;
  }
}

// ---------------------------------------------------------------------------
// Tablet / desktop: show 3 cards at once — selected in the middle, the
// previous/next peeking on each side in an "unselected" state.
// ---------------------------------------------------------------------------
@media (min-width: 768px) {
  .ca-overlapping-carousel__viewport {
    justify-content: center;
    gap: var(--size-4);
  }

  .ca-overlapping-carousel__stage {
    // Tablet/desktop: drop the native scroll and rebuild the absolute overlap.
    display: block;
    overflow: clip visible;
    scroll-snap-type: none;
    overscroll-behavior-inline: auto;
    padding-inline: 0;
    scroll-padding-inline: 0;
    // Back inside the grid (no full-bleed); shrink to leave room for the side
    // arrows so it never overflows the container.
    flex: 0 1 760px;
    width: auto;
    height: 600px;
    max-width: 760px;
    margin-inline: 0;
    --side-shift: 33%;
    --side-scale: 0.86;
    --oc-ease: cubic-bezier(0.15, 0, 0.2, 1);
    --oc-duration: 0.65s;
  }

  // Rebuild each card as an absolute ring member with the sliding transition.
  .ca-overlapping-carousel__card {
    position: absolute;
    top: 0;
    left: 50%;
    width: 420px;
    height: 100%;
    transform: translateX(-50%);
    transform-origin: center;
    scroll-snap-align: none;
    transition:
      transform var(--oc-duration) var(--oc-ease),
      opacity calc(var(--oc-duration) * 0.35) ease,
      z-index 0s linear calc(var(--oc-duration) * var(--oc-z-swap));
    will-change: transform;
  }

  .ca-overlapping-carousel__card--active {
    z-index: 3;
    transform: translateX(-50%) scale(1);
    opacity: 1;
  }

  .ca-overlapping-carousel__card--prev {
    z-index: 2;
    transform: translateX(calc(-50% - var(--side-shift)))
      scale(var(--side-scale));
    opacity: 0.5;
  }

  .ca-overlapping-carousel__card--next {
    z-index: 2;
    transform: translateX(calc(-50% + var(--side-shift)))
      scale(var(--side-scale));
    opacity: 0.5;
  }

  // Retired cards recede to the centre behind the active one and stay hidden.
  .ca-overlapping-carousel__card--back-prev,
  .ca-overlapping-carousel__card--back-next {
    z-index: 1;
    opacity: 0;
    pointer-events: none;
    transform: translateX(-50%) scale(var(--back-scale));
    transition:
      transform var(--oc-duration) var(--oc-ease),
      opacity calc(var(--oc-duration) * 0.25) ease
        calc(var(--oc-duration) * 0.45),
      z-index 0s linear calc(var(--oc-duration) * var(--oc-z-swap));
  }

  // Non-selected cards are not interactive.
  .ca-overlapping-carousel__card:not(.ca-overlapping-carousel__card--active) {
    pointer-events: none;
  }

  // Reveal the side arrows on tablet/desktop (size-8 gap set on the viewport).
  .ca-overlapping-carousel__arrow {
    display: inline-flex;
  }
}

// 768–830: aperta o desvio das cartas laterais.
@media (min-width: 768px) and (max-width: 830px) {
  .ca-overlapping-carousel__stage {
    --side-shift: 27%;
  }
}

// Desktop: spread the side cards further so more of each one shows.
@media (min-width: 1025px) {
  .ca-overlapping-carousel__viewport {
    justify-content: center;
    gap: var(--size-8);
  }

  .ca-overlapping-carousel__stage {
    // Grow to fill the viewport so the arrows sit at its edges (size-8 gap).
    flex: 1 1 auto;
    max-width: none;
    --side-shift: 70%;
    --side-scale: 0.9;
  }
}

// Ajuste fino do desvio lateral por faixa de largura (desktop).
@media (min-width: 1025px) and (max-width: 1099px) {
  .ca-overlapping-carousel__stage {
    --side-shift: 46%;
  }
}

@media (min-width: 1100px) and (max-width: 1139px) {
  .ca-overlapping-carousel__stage {
    --side-shift: 55%;
  }
}

@media (min-width: 1140px) and (max-width: 1181px) {
  .ca-overlapping-carousel__stage {
    --side-shift: 60%;
  }
}

@media (min-width: 1182px) and (max-width: 1226px) {
  .ca-overlapping-carousel__stage {
    --side-shift: 65%;
  }
}

@media (min-width: 1227px) and (max-width: 1249px) {
  .ca-overlapping-carousel__stage {
    --side-shift: 50%;
  }
}

// Re-tint the dots for a light background (the DS defaults target dark ones).
.ca-overlapping-carousel__dots {
  // Container gap is --size-10; pull the dots up so the viewport→dots gap is --size-4.
  margin-top: calc(var(--size-5) - var(--size-8));

  :deep(.ca-page-control__dot) {
    background-color: var(--ca-color-neutrals-on-background-low-30);
  }

  :deep(.ca-page-control__dot--active) {
    background-color: var(--ca-color-theme-primary, #066542);
  }
}

// Desktop: same viewport→dots gap calc but with --size-7 (container gap is --size-10).
// Placed after the base rule so it wins by source order at >1025px.
@media (min-width: 1025px) {
  .ca-overlapping-carousel__dots {
    margin-top: calc(var(--size-7) - var(--size-10));
  }
}

// ---------------------------------------------------------------------------
// Reduced motion: drop the travel so switching cards becomes a plain crossfade,
// while the static depth cues (smaller side cards) stay.
// ---------------------------------------------------------------------------
@media (prefers-reduced-motion: reduce) {
  .ca-overlapping-carousel__card,
  .ca-overlapping-carousel__card--back-prev,
  .ca-overlapping-carousel__card--back-next {
    transition: opacity 0.2s ease;
  }

  .ca-overlapping-carousel__media,
  .ca-overlapping-carousel__veil {
    transition-delay: 0ms;
  }
}
</style>
